import React, {useEffect, useState} from "react";
import {Alert, Button, Form, Modal} from "react-bootstrap";
import axiosInstance from "../axiosInstance";
import config from "../Config/config";

const ForgetPasswordModal = ({show, handleClose}) => {
    const [step, setStep] = useState(1);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [otp, setOtp] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [resendOtpTimer, setResendOtpTimer] = useState(60);

// Start the OTP timer
    useEffect(() => {
        let timer;
        if (resendOtpTimer > 0) {
            timer = setInterval(() => {
                setResendOtpTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [resendOtpTimer]);

    // Handle phone number submission
    const handlePhoneSubmit = () => {
        if (!phoneNumber) {
            setError("Please enter your phone number.");
            return;
        }

        let formattedPhoneNumber = phoneNumber.trim();
        if (!formattedPhoneNumber.startsWith("+91")) {
            formattedPhoneNumber = `+91${formattedPhoneNumber}`;
        }
        setPhoneNumber(formattedPhoneNumber);
        axiosInstance
            .get(`${config.api.otp}?number=${formattedPhoneNumber}`)
            .then((res) => {
                if (res.status === 200) {
                    setStep(2);
                    setResendOtpTimer(60);
                    setSuccess("OTP sent successfully!");
                    setError("");
                }
            })
            .catch((err) => {
                setError(err.response?.data || "An unexpected error occurred.");
            });
    };

    // Handle OTP verification
    const handleOtpSubmit = () => {
        if (!otp) {
            setError("Please enter the OTP.");
            return;
        }

        axiosInstance
            .post(`${config.api.otp}`, {
                phone_number: phoneNumber,
                otp: otp,
            })
            .then((res) => {
                if (res.status === 202) {
                    setStep(3);
                    setError("");
                    setSuccess("OTP verified successfully!");
                }
            })
            .catch((err) => {
                setSuccess("");
                setError(err.response?.data || "Invalid OTP, please try again.");
            });
    };

    // Handle password reset
    const handlePasswordSubmit = () => {
        if (!password || !confirmPassword) {
            setError("Both password fields are required.");
            return;
        }
        if (password.length < 8) {
            setError("Password must be at least 8 characters long.");
            return;
        }
        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        axiosInstance
            .post(`${config.api.otp}?number=${phoneNumber}`, {
                passWord: password,
            })
            .then((res) => {
                if (res.status === 200) {
                    setSuccess("Password reset successfully! Redirecting...");
                    setTimeout(() => {
                        handleClose();
                    }, 1000);
                }
            })
            .catch((err) => {
                setError(err.response?.data || "An error occurred. Please try again.");
            });
    };

    // Handle OTP Resend
    const resendOtp = () => {
        if (resendOtpTimer > 0) return;

        axiosInstance
            .get(`${config.api.otp}?number=${phoneNumber}`)
            .then((res) => {
                if (res.status === 200) {
                    setResendOtpTimer(60);
                    setSuccess("OTP resent successfully!");
                    setError("");
                }
            })
            .catch((err) => {
                setError(err.response?.data || "Failed to resend OTP.");
            });
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {step === 1
                        ? "Phone Verification"
                        : step === 2
                            ? "OTP Verification"
                            : "Reset Password"}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                {step === 1 && (
                    <Form>
                        <Form.Group controlId="phone">
                            <Form.Label>Phone Number</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter your phone number"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                            />
                        </Form.Group>
                        <Button
                            className="mt-3 w-50"
                            variant="primary"
                            onClick={handlePhoneSubmit}
                        >
                            Send OTP
                        </Button>
                    </Form>
                )}

                {step === 2 && (
                    <Form>
                        <p>The OTP was sent to {phoneNumber}</p>
                        <Form.Group controlId="otp">
                            <Form.Label>Enter OTP</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter OTP"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                            />
                        </Form.Group>
                        <Button
                            className="mt-3 w-50"
                            variant="primary"
                            onClick={handleOtpSubmit}
                        >
                            Verify OTP
                        </Button>
                        {resendOtpTimer === 0 ? (
                            <Button variant="link" className="mt-3" onClick={resendOtp}>
                                Resend OTP
                            </Button>
                        ) : (
                            <p className="text-muted mt-3 text-center">
                                Resend OTP in {resendOtpTimer} seconds
                            </p>
                        )}
                    </Form>
                )}

                {step === 3 && (
                    <Form>
                        <p>Please enter a new password</p>
                        <Form.Group controlId="password">
                            <Form.Label>New Password</Form.Label>
                            <Form.Control
                                type="password"
                                placeholder="Enter new password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group controlId="confirmPassword" className="mt-3">
                            <Form.Label>Confirm Password</Form.Label>
                            <Form.Control
                                type="password"
                                placeholder="Confirm new password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                            />
                        </Form.Group>
                        <Button
                            className="mt-3 w-50"
                            variant="primary"
                            onClick={handlePasswordSubmit}
                        >
                            Reset Password
                        </Button>
                    </Form>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ForgetPasswordModal;
